/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/components/LocatorField.tsx
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)      | Who               | Why              | What Changed                    |
 * |---|------------------------|-------------------|------------------|---------------------------------|
 * | 1 | 2026-06-19 (fix)       | Claude Sonnet 4.6 | Locator UX fix   | Created — plain-language selector editor |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/App.tsx  (<LocatorField />) used in StepEditForm
 *
 * EXPORTS
 * -------
 * LocatorField   — inline selector editor (strategy dropdown + value input)
 * LOCATOR_STRATEGIES — exported so CommandToolbar can reuse the strategy list
 * LocatorStrategy   — the union of supported strategy strings
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. Only exposes the 4 strategies that are confirmed to work in resolver.ts:
 *    text, textContains, a11yId, contentDesc. Other strategies (xpath, css,
 *    iosPredicate) are stubbed in the resolver and would always return false.
 * 2. Writes to selector.candidates[0] only. Existing candidates[1..n] (from
 *    the recorder or previous heals) are preserved — this component only
 *    manages the "primary" candidate at index 0.
 * 3. When value is empty → candidates[0] is removed → candidates may be [].
 *    Parents should guard against saving with empty candidates.
 * 4. description auto-syncs to value unless showLabel=true (then it's
 *    independently controlled via a "Step label" input).
 *
 * =============================================================================
 */

import { useState, useEffect } from "react";
import type { Selector, Candidate } from "@mimiq/ir";
import { getXPathQualityFromString } from "../device/xpathUtils.js";
import type { XPathQuality } from "../device/xpathUtils.js";

// ── Strategy definitions ───────────────────────────────────────────────────

export const LOCATOR_STRATEGIES = [
  {
    strategy: "text",
    label:    "Text on screen",
    placeholder: 'e.g. Log In',
    hint: undefined as string | undefined,
    isXPath: false,
  },
  {
    strategy: "textContains",
    label:    "Text contains",
    placeholder: 'e.g. Log',
    hint: "Matches any element whose text includes this string",
    isXPath: false,
  },
  {
    strategy: "a11yId",
    label:    "Accessibility ID",
    placeholder: 'e.g. login_button',
    hint: "Resource ID (for developers): com.example.app:id/login_btn",
    isXPath: false,
  },
  {
    strategy: "contentDesc",
    label:    "Content description",
    placeholder: 'e.g. Search',
    hint: "Accessibility label — often set on icon-only buttons",
    isXPath: false,
  },
  {
    strategy: "xpath",
    label:    "XPath (Advanced)",
    placeholder: '//RecyclerView[@resource-id="..."]/ViewGroup[@index="1"]',
    hint: "XPath expression — auto-generated for list rows via Inspect Mode. Usable in Appium too.",
    isXPath: true,
  },
] as const;

export type LocatorStrategy = typeof LOCATOR_STRATEGIES[number]["strategy"];

// ── Props ──────────────────────────────────────────────────────────────────

export interface LocatorFieldProps {
  selector: Selector;
  onChange: (updated: Selector) => void;
  autoFocus?: boolean;
  /** When true, renders an additional "Step label" input so the user can
   *  give the step a meaningful display name independent of the locator value. */
  showLabel?: boolean;
}

// ── Component ──────────────────────────────────────────────────────────────

export function LocatorField({ selector, onChange, autoFocus, showLabel }: LocatorFieldProps) {
  const firstCandidate = selector.candidates[0] as Candidate | undefined;

  const initialStrategy: LocatorStrategy =
    firstCandidate && LOCATOR_STRATEGIES.some((s) => s.strategy === firstCandidate.strategy)
      ? firstCandidate.strategy as LocatorStrategy
      : "text";

  const [strategy, setStrategy] = useState<LocatorStrategy>(initialStrategy);
  const [value, setValue]       = useState(firstCandidate?.value ?? "");
  const [label, setLabel]       = useState(selector.description === "element" ? "" : selector.description);

  // Sync outward on every change
  useEffect(() => {
    const trimmed = value.trim();
    const candidate: Candidate | undefined = trimmed
      ? { strategy, value: trimmed, addedBy: "user" }
      : undefined;

    // Preserve candidates[1..n] (recorder/heal candidates) if they exist
    const tail = selector.candidates.slice(1);
    const candidates: Candidate[] = candidate ? [candidate, ...tail] : tail;

    // Description: use the explicit label if set; else fall back to value
    const description = (showLabel ? label.trim() : undefined) || trimmed || "element";

    onChange({ ...selector, description, candidates });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategy, value, label]);

  const strategyMeta = LOCATOR_STRATEGIES.find((s) => s.strategy === strategy)!;
  const isXPath = strategyMeta.isXPath;

  // For XPath strategy, compute quality from the entered value
  const xpathQuality: XPathQuality | null = isXPath && value.trim()
    ? getXPathQualityFromString(value.trim())
    : null;

  return (
    <div className="lf-wrapper">
      {showLabel && (
        <div className="sef-row">
          <label className="sef-label">Step label:</label>
          <input
            className="sef-input"
            placeholder="e.g. Login button (optional)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>
      )}
      <div className="sef-row">
        <label className="sef-label">Find element:</label>
        <select
          className="sef-select lf-strategy"
          value={strategy}
          onChange={(e) => { setStrategy(e.target.value as LocatorStrategy); setValue(""); }}
          autoFocus={autoFocus && !showLabel}
        >
          {LOCATOR_STRATEGIES.map((s) => (
            <option key={s.strategy} value={s.strategy}>{s.label}</option>
          ))}
        </select>
        {isXPath ? (
          /* XPath gets a textarea so long expressions are readable */
          <textarea
            className="sef-input lf-xpath-input"
            placeholder={strategyMeta.placeholder}
            value={value}
            autoFocus={autoFocus && showLabel}
            onChange={(e) => setValue(e.target.value)}
            rows={2}
          />
        ) : (
          <input
            className="sef-input lf-value"
            placeholder={strategyMeta.placeholder}
            value={value}
            autoFocus={autoFocus && showLabel}
            onChange={(e) => setValue(e.target.value)}
          />
        )}
      </div>
      {/* XPath quality badge + explanation */}
      {xpathQuality && (
        <div className="lf-xpath-quality">
          {xpathQuality === "stable" && (
            <span className="lf-quality-badge lf-quality-badge--stable">stable</span>
          )}
          {xpathQuality === "indexed" && (
            <span className="lf-quality-badge lf-quality-badge--indexed">⚡ indexed</span>
          )}
          {xpathQuality === "fragile" && (
            <span className="lf-quality-badge lf-quality-badge--fragile">⚠ fragile</span>
          )}
          <span className="lf-xpath-quality-note">
            {xpathQuality === "stable" && "Uses a unique attribute — most stable."}
            {xpathQuality === "indexed" && "Uses @index for position — stable as long as list order doesn't change."}
            {xpathQuality === "fragile" && "Positional path — will break when UI structure changes. Prefer text or resource-id."}
          </span>
        </div>
      )}
      {strategyMeta.hint && !xpathQuality && (
        <p className="lf-hint">{strategyMeta.hint}</p>
      )}
      {value.trim() === "" && (
        <p className="lf-required">Enter a value — this field is required to find the element at replay.</p>
      )}
    </div>
  );
}
